/**
 * Componentes compartilhados da linguagem visual nova (rodada de modernização
 * das listas): item-pessoa com avatar, tabela densa sem bordas verticais, menu
 * de ações "três pontinhos", texto truncado com tooltip e badge de estado.
 * Regra da casa: NENHUM link azul sublinhado como conteúdo principal — navegação
 * por item clicável ou ação explícita em botão/ícone.
 */
import { useEffect, useState, type ReactNode } from "react";
import { ActionIcon, Avatar, Badge, Card, Group, Menu, Table, Text, Tooltip } from "@mantine/core";
import { IconDots } from "@tabler/icons-react";
import { tokenAtual, urlComToken } from "../api";
import { ROTULO_ESTADO } from "../format";

// ---------------------------------------------------------------------------
// ItemPessoa — item moderno para listas de entidades-pessoa
// ---------------------------------------------------------------------------

const CORES_AVATAR = ["sillis", "teal", "cyan", "indigo", "violet", "grape", "blue", "green"] as const;

/** Cor determinística do nome (hash simples → paleta). */
export function corDoNome(nome: string): string {
  let h = 0;
  for (let i = 0; i < nome.length; i++) h = (h * 31 + nome.charCodeAt(i)) >>> 0;
  return CORES_AVATAR[h % CORES_AVATAR.length]!;
}

/** Iniciais do nome (duas primeiras palavras). */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const a = partes[0]?.[0] ?? "?";
  const b = partes.length > 1 ? partes[1]![0] : "";
  return (a + b).toUpperCase();
}

// ---------------------------------------------------------------------------
// AvatarPessoa — foto do cadastro quando existe; fallback = iniciais coloridas
// ---------------------------------------------------------------------------

/** Cache por sessão (funcionarioId → url da foto ou null = sem foto). */
const cacheFotos = new Map<string, string | null>();

/** Invalida o cache da foto da pessoa (após upload/remoção). */
export function invalidarCacheFoto(funcionarioId: string): void {
  cacheFotos.delete(funcionarioId);
}

/** Avatar com a FOTO do cadastro quando existe (1 meta por pessoa por sessão;
 *  o carimbo da foto quebra o cache do navegador quando ela muda). */
export function AvatarPessoa({
  funcionarioId,
  nome,
  size = "md",
  radius = "xl",
}: {
  funcionarioId: string;
  nome: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | number;
  radius?: "xs" | "sm" | "md" | "lg" | "xl";
}) {
  const [url, setUrl] = useState<string | null | undefined>(cacheFotos.get(funcionarioId));
  useEffect(() => {
    const cacheada = cacheFotos.get(funcionarioId);
    if (cacheada !== undefined) {
      setUrl(cacheada);
      return;
    }
    let vivo = true;
    const token = tokenAtual();
    fetch(urlComToken(`/funcionarios/${funcionarioId}/foto/meta`), {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then(async (r) => {
        let u: string | null = null;
        if (r.ok) {
          const meta = (await r.json()) as { atualizadoEm: string };
          u = urlComToken(`/funcionarios/${funcionarioId}/foto?v=${encodeURIComponent(meta.atualizadoEm)}`);
        }
        cacheFotos.set(funcionarioId, u);
        if (vivo) setUrl(u);
      })
      .catch(() => {
        cacheFotos.set(funcionarioId, null);
        if (vivo) setUrl(null);
      });
    return () => {
      vivo = false;
    };
  }, [funcionarioId]);
  return (
    <Avatar src={url ?? undefined} color={corDoNome(nome)} radius={radius} size={size} alt={`Foto de ${nome}`}>
      {iniciais(nome)}
    </Avatar>
  );
}

/** Item clicável de lista de pessoas: avatar com iniciais/cor do nome, nome
 *  forte (NÃO link), linha secundária de metadados, badges à direita. */
export function ItemPessoa({
  nome,
  linhaSecundaria,
  badges,
  onClick,
  opaco,
  funcionarioId,
}: {
  nome: string;
  linhaSecundaria?: ReactNode;
  badges?: ReactNode;
  onClick: () => void;
  /** Atenuado visualmente (ex.: inativo). */
  opaco?: boolean;
  /** Quando informado, o avatar exibe a FOTO do cadastro (fallback = iniciais). */
  funcionarioId?: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      mb={6}
      role="button"
      tabIndex={0}
      aria-label={`Abrir detalhe de ${nome}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "pointer",
        opacity: opaco ? 0.55 : 1,
        transition: "box-shadow 120ms ease, border-color 120ms ease",
        boxShadow: hover ? "var(--mantine-shadow-sm)" : undefined,
        borderColor: hover ? "var(--mantine-color-sillis-4)" : undefined,
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          {funcionarioId ? (
            <AvatarPessoa funcionarioId={funcionarioId} nome={nome} />
          ) : (
            <Avatar color={corDoNome(nome)} radius="xl" size="md">
              {iniciais(nome)}
            </Avatar>
          )}
          <div style={{ minWidth: 0 }}>
            <Text fw={600} size="sm" truncate>
              {nome}
            </Text>
            {linhaSecundaria && (
              <Text size="xs" c="dimmed" truncate>
                {linhaSecundaria}
              </Text>
            )}
          </div>
        </Group>
        {badges && (
          <Group gap={6} wrap="nowrap">
            {badges}
          </Group>
        )}
      </Group>
    </Card>
  );
}

/** Item genérico de lista de ENTIDADES (o que a pessoa "abre para trabalhar":
 *  competência, evento, fórmula, tomador, empréstimo…): slot de ícone/avatar,
 *  título forte, linha secundária de metadados, badges e menu de ações — hover
 *  com elevação, clique abre o detalhe. */
export function ItemEntidade({
  icone,
  titulo,
  linhaSecundaria,
  badges,
  acoes,
  onClick,
  opaco,
  ariaLabel,
}: {
  /** Slot visual à esquerda (ThemeIcon, Badge de código, AvatarPessoa…). */
  icone: ReactNode;
  titulo: ReactNode;
  linhaSecundaria?: ReactNode;
  badges?: ReactNode;
  /** Ações do menu "três pontinhos" (clique NÃO propaga para o item). */
  acoes?: AcaoLinha[];
  onClick?: () => void;
  opaco?: boolean;
  ariaLabel?: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      mb={6}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: onClick ? "pointer" : undefined,
        opacity: opaco ? 0.55 : 1,
        transition: "box-shadow 120ms ease, border-color 120ms ease",
        boxShadow: hover ? "var(--mantine-shadow-sm)" : undefined,
        borderColor: hover ? "var(--mantine-color-sillis-4)" : undefined,
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          {icone}
          <div style={{ minWidth: 0 }}>
            <Text fw={600} size="sm" truncate component="div">
              {titulo}
            </Text>
            {linhaSecundaria && (
              <Text size="xs" c="dimmed" truncate component="div">
                {linhaSecundaria}
              </Text>
            )}
          </div>
        </Group>
        <Group gap={6} wrap="nowrap">
          {badges}
          {acoes && acoes.length > 0 && (
            <span onClick={(e) => e.stopPropagation()}>
              <MenuAcoes acoes={acoes} />
            </span>
          )}
        </Group>
      </Group>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BadgeEstado — badge de estado da competência (cor semântica única do app)
// ---------------------------------------------------------------------------

const COR_ESTADO_COMP: Record<string, string> = {
  aberta: "blue",
  em_calculo: "yellow",
  conferida: "teal",
  fechada: "gray",
  retificada: "violet",
};

export function BadgeEstado({ estado, size = "md" }: { estado: string; size?: "xs" | "sm" | "md" | "lg" }) {
  return (
    <Badge size={size} color={COR_ESTADO_COMP[estado] ?? "gray"}>
      {ROTULO_ESTADO[estado] ?? estado}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// TabelaDados — tabela densa sem bordas verticais (cabeçalho caps cinza)
// ---------------------------------------------------------------------------

export function TabelaDados({ children, ...props }: { children: ReactNode } & Record<string, unknown>) {
  return (
    <Table striped highlightOnHover withTableBorder={false} withColumnBorders={false} horizontalSpacing="sm" verticalSpacing={7} {...props}>
      {children}
    </Table>
  );
}

/** Célula de cabeçalho da TabelaDados (caps pequeno cinza; num = direita). */
export function Th({ children, num }: { children?: ReactNode; num?: boolean }) {
  return (
    <Table.Th
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontWeight: 700,
        color: "var(--mantine-color-gray-6)",
        textAlign: num ? "right" : "left",
        borderBottom: "1px solid var(--mantine-color-gray-3)",
      }}
    >
      {children}
    </Table.Th>
  );
}

/** Célula numérica tabular à direita. */
export function TdNum({ children }: { children: ReactNode }) {
  return (
    <Table.Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{children}</Table.Td>
  );
}

// ---------------------------------------------------------------------------
// MenuAcoes — ações da linha em "três pontinhos" (em vez de links/botões cheios)
// ---------------------------------------------------------------------------

export interface AcaoLinha {
  rotulo: string;
  icone?: ReactNode;
  cor?: string;
  onClick: () => void;
  disabled?: boolean;
}

export function MenuAcoes({ acoes, rotulo = "Ações" }: { acoes: AcaoLinha[]; rotulo?: string }) {
  return (
    <Menu shadow="md" width={220} position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" aria-label={rotulo} size="sm">
          <IconDots size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {acoes.map((a) => (
          <Menu.Item key={a.rotulo} leftSection={a.icone} color={a.cor} disabled={a.disabled} onClick={a.onClick}>
            {a.rotulo}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// TextoTruncado — texto longo truncado com tooltip do conteúdo completo
// ---------------------------------------------------------------------------

export function TextoTruncado({ texto, max = 90, vazio = "—" }: { texto: string | null | undefined; max?: number; vazio?: string }) {
  if (!texto) {
    return (
      <Text size="xs" c="dimmed">
        {vazio}
      </Text>
    );
  }
  if (texto.length <= max) {
    return (
      <Text size="xs" c="dimmed">
        {texto}
      </Text>
    );
  }
  return (
    <Tooltip label={texto} multiline maw={440} withArrow>
      <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
        {texto.slice(0, max).trimEnd()}…
      </Text>
    </Tooltip>
  );
}
